import { useMemo } from 'react';
import Select, { createFilter } from 'react-select';
import Button from 'src/components/Button';
import { FieldKindToggle } from 'src/components/FieldKindToggle';
import { registeredCustomFieldOptions } from 'src/components/admin/encompassSelectableFields';
import { useRegisteredCustomFields } from 'src/queries/encompass/useRegisteredCustomFields';
import { useEncompassFieldSchema } from 'src/queries/encompass/useEncompassFieldSchema';
import {
  ENCOMPASS_FIELD_KIND,
  getEncompassFieldKind,
  isCustomEncompassFieldKind,
  type EncompassFieldKind,
} from 'src/services/encompass/getEncompassCustomFields';
import { LoanChecklistRuleCondition } from 'src/services/loans/types';
import { ruleSelectStyles, ruleSelectComponents } from './ruleSelectConfig';

type SelectOption = { value: string; label: string };
type ConditionEditorLayout = 'wide' | 'compact';

const inputClass =
  'block h-8 w-full rounded border border-gray-300 bg-white px-2 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
const fastFilter = createFilter({ ignoreAccents: false });
const operatorOptions: SelectOption[] = [
  { value: 'and', label: 'And' },
  { value: 'or', label: 'Or' },
];

function ConditionOperatorSelect({
  inputId,
  index,
  value,
  onChange,
}: Readonly<{
  inputId: string;
  index: number;
  value: 'and' | 'or';
  onChange: (value: 'and' | 'or') => void;
}>) {
  return (
    <Select<SelectOption>
      inputId={inputId}
      aria-label={`Condition operator ${index + 1}`}
      isSearchable={false}
      options={operatorOptions}
      value={operatorOptions.find((option) => option.value === value)}
      onChange={(option) => onChange((option?.value ?? 'and') as 'and' | 'or')}
      className="text-xs"
      classNamePrefix="encompass-condition-operator"
      components={ruleSelectComponents}
      menuPortalTarget={typeof document === 'undefined' ? null : document.body}
      menuPosition="fixed"
      menuPlacement="auto"
      maxMenuHeight={220}
      styles={ruleSelectStyles}
    />
  );
}

const conditionLabel = (condition: LoanChecklistRuleCondition) =>
  condition.fieldLabel || condition.field;

const conditionFieldKind = (
  condition: LoanChecklistRuleCondition,
): EncompassFieldKind => getEncompassFieldKind(condition.fieldKind);

const optionKey = (kind: EncompassFieldKind, fieldId: string) =>
  `${kind}:${fieldId.trim()}`;

type EncompassConditionField = {
  id?: string;
  description?: string | null;
  dataType?: string | null;
  options?: Array<{ value?: string | null; text?: string | null }> | null;
};

type FieldGroup = {
  kind: EncompassFieldKind;
  fields: readonly EncompassConditionField[];
};

const fieldLabel = (fieldId: string, description?: string | null) =>
  description ? `${fieldId} - ${description}` : fieldId;

function buildConditionFieldOptions(
  kind: EncompassFieldKind,
  fields: readonly EncompassConditionField[],
  conditions: readonly LoanChecklistRuleCondition[],
): SelectOption[] {
  const options = fields.flatMap((field) => {
    const fieldId = field.id?.trim() ?? '';
    return fieldId
      ? [{ value: fieldId, label: fieldLabel(fieldId, field.description) }]
      : [];
  });
  const ids = new Set(options.map((option) => option.value));

  conditions
    .filter((condition) => conditionFieldKind(condition) === kind)
    .forEach((condition) => {
      if (!condition.field || ids.has(condition.field)) return;
      options.push({
        value: condition.field,
        label: conditionLabel(condition),
      });
    });

  return options.sort((a, b) => a.label.localeCompare(b.label));
}

const toValueOptions = (
  options: EncompassConditionField['options'],
): SelectOption[] => {
  const seen = new Set<string>();
  return (options ?? []).flatMap((option) => {
    const value = option.value?.trim() ?? '';
    if (!value || seen.has(value)) return [];
    seen.add(value);
    return [{ value, label: option.text?.trim() || value }];
  });
};

function buildFieldMetadataMaps(fieldGroups: readonly FieldGroup[]) {
  const valueOptionsByKey = new Map<string, SelectOption[]>();
  const dataTypeByKey = new Map<string, string>();

  for (const { kind, fields } of fieldGroups) {
    for (const field of fields) {
      const fieldId = field.id?.trim();
      if (!fieldId) continue;

      dataTypeByKey.set(optionKey(kind, fieldId), field.dataType ?? '');
      const choices = toValueOptions(field.options);
      if (choices.length > 0) {
        valueOptionsByKey.set(optionKey(kind, fieldId), choices);
      }
    }
  }

  return { valueOptionsByKey, dataTypeByKey };
}

function EncompassFieldValueInput({
  inputId,
  fieldId,
  value,
  onChange,
  optionChoices,
  useDateInput,
}: Readonly<{
  inputId: string;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  optionChoices: SelectOption[];
  useDateInput: boolean;
}>) {
  if (optionChoices.length > 0) {
    const selected =
      optionChoices.find((option) => option.value === value) ??
      (value ? { value, label: value } : null);

    return (
      <Select<SelectOption>
        inputId={inputId}
        aria-label="Expected field value"
        isClearable
        options={optionChoices}
        value={selected}
        onChange={(option) => onChange(option?.value ?? '')}
        placeholder="Select allowed value..."
        className="text-xs"
        classNamePrefix="encompass-condition-value"
        components={ruleSelectComponents}
        menuPortalTarget={
          typeof document === 'undefined' ? null : document.body
        }
        menuPosition="fixed"
        menuPlacement="auto"
        maxMenuHeight={220}
        styles={ruleSelectStyles}
      />
    );
  }

  let placeholder: string | undefined;
  if (!useDateInput) {
    placeholder = fieldId ? 'Exact match value' : 'Expected value';
  }

  return (
    <input
      id={inputId}
      type={useDateInput ? 'date' : 'text'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  );
}

export function EncompassFieldConditionEditor({
  conditions,
  onChange,
  idPrefix,
  layout = 'wide',
}: Readonly<{
  conditions: LoanChecklistRuleCondition[];
  onChange: (conditions: LoanChecklistRuleCondition[]) => void;
  idPrefix: string;
  /** Wide is for the global-checklist modal; compact is for a rule side panel. */
  layout?: ConditionEditorLayout;
}>) {
  const fieldSchemaQuery = useEncompassFieldSchema();
  const customFieldsQuery = useRegisteredCustomFields(
    conditions.some((condition) =>
      isCustomEncompassFieldKind(conditionFieldKind(condition)),
    ),
  );

  const customFields = useMemo(
    () => registeredCustomFieldOptions(customFieldsQuery.data),
    [customFieldsQuery.data],
  );

  const standardFields = useMemo(
    () => fieldSchemaQuery.data?.fields ?? [],
    [fieldSchemaQuery.data?.fields],
  );
  const standardFieldOptions = useMemo<SelectOption[]>(
    () =>
      buildConditionFieldOptions(
        ENCOMPASS_FIELD_KIND.STANDARD,
        standardFields,
        conditions,
      ),
    [conditions, standardFields],
  );

  const customFieldOptions = useMemo<SelectOption[]>(
    () =>
      buildConditionFieldOptions(
        ENCOMPASS_FIELD_KIND.CUSTOM,
        customFields,
        conditions,
      ),
    [conditions, customFields],
  );

  const { valueOptionsByKey, dataTypeByKey } = useMemo(
    () =>
      buildFieldMetadataMaps([
        { kind: ENCOMPASS_FIELD_KIND.STANDARD, fields: standardFields },
        { kind: ENCOMPASS_FIELD_KIND.CUSTOM, fields: customFields },
      ]),
    [customFields, standardFields],
  );

  const updateCondition = (
    index: number,
    updates: Partial<LoanChecklistRuleCondition>,
  ) =>
    onChange(
      conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, ...updates } : condition,
      ),
    );

  return (
    <div className="flex flex-col gap-3">
      {conditions.map((condition, index) => {
        const fieldKind = conditionFieldKind(condition);
        const fieldOptions = isCustomEncompassFieldKind(fieldKind)
          ? customFieldOptions
          : standardFieldOptions;
        const valueKey = optionKey(fieldKind, condition.field);

        return (
          <div
            key={`${idPrefix}-${index}`}
            className={
              layout === 'compact'
                ? 'grid grid-cols-2 gap-2 rounded-md border border-gray-200 bg-gray-50 p-3'
                : 'grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 sm:grid-cols-6'
            }
          >
            <div
              className={
                layout === 'compact'
                  ? 'col-span-2 flex flex-col gap-2'
                  : 'flex flex-col gap-2 sm:col-span-2'
              }
            >
              <label
                htmlFor={`${idPrefix}-field-${index}`}
                className="text-sm font-medium text-gray-900"
              >
                Encompass field
              </label>
              <div className="flex items-center">
                <FieldKindToggle
                  value={fieldKind}
                  onChange={(kind) =>
                    updateCondition(index, {
                      fieldKind: isCustomEncompassFieldKind(kind)
                        ? kind
                        : undefined,
                      field: '',
                      fieldLabel: undefined,
                      value: '',
                    })
                  }
                  ariaLabel="Encompass field type"
                  size="sm"
                />
              </div>
              <Select<SelectOption>
                inputId={`${idPrefix}-field-${index}`}
                aria-label={`Encompass field condition ${index + 1}`}
                isClearable
                isLoading={
                  isCustomEncompassFieldKind(fieldKind)
                    ? customFieldsQuery.isLoading
                    : fieldSchemaQuery.isLoading
                }
                options={fieldOptions}
                value={
                  fieldOptions.find(
                    (option) => option.value === condition.field,
                  ) ?? null
                }
                onChange={(option) => {
                  const field = option?.value ?? '';
                  updateCondition(index, {
                    field,
                    fieldKind: isCustomEncompassFieldKind(fieldKind)
                      ? fieldKind
                      : undefined,
                    fieldLabel: option?.label ?? undefined,
                    value: field === condition.field ? condition.value : '',
                  });
                }}
                placeholder="Search field..."
                className="text-xs"
                classNamePrefix="encompass-condition-field"
                components={ruleSelectComponents}
                filterOption={fastFilter}
                menuPortalTarget={
                  typeof document === 'undefined' ? null : document.body
                }
                menuPosition="fixed"
                menuPlacement="auto"
                maxMenuHeight={220}
                styles={ruleSelectStyles}
              />
              {isCustomEncompassFieldKind(fieldKind) &&
                customFieldsQuery.isSuccess &&
                customFieldOptions.length === 0 && (
                  <p className="text-[10px] text-gray-500">
                    Add a custom field through Admin Mapping first.
                  </p>
                )}
            </div>
            <div
              className={
                layout === 'compact'
                  ? 'col-span-2 flex flex-col gap-2'
                  : 'flex flex-col gap-2 sm:col-span-2'
              }
            >
              <label
                htmlFor={`${idPrefix}-value-${index}`}
                className="text-sm font-medium text-gray-900"
              >
                Value
              </label>
              <EncompassFieldValueInput
                inputId={`${idPrefix}-value-${index}`}
                fieldId={condition.field}
                value={condition.value}
                onChange={(value) => updateCondition(index, { value })}
                optionChoices={valueOptionsByKey.get(valueKey) ?? []}
                useDateInput={
                  (dataTypeByKey.get(valueKey) ?? '').trim().toLowerCase() ===
                  'datetime'
                }
              />
            </div>
            <div className="flex flex-col gap-2 sm:col-span-1">
              <label
                htmlFor={`${idPrefix}-operator-${index}`}
                className="text-sm font-medium text-gray-900"
              >
                Operator
              </label>
              <ConditionOperatorSelect
                inputId={`${idPrefix}-operator-${index}`}
                index={index}
                value={condition.operator}
                onChange={(operator) => updateCondition(index, { operator })}
              />
            </div>
            <div className="flex items-end justify-end sm:col-span-1">
              <Button
                variant="outline"
                onClick={() =>
                  onChange(
                    conditions.filter(
                      (_, conditionIndex) => conditionIndex !== index,
                    ),
                  )
                }
              >
                Remove
              </Button>
            </div>
          </div>
        );
      })}
      {(fieldSchemaQuery.isError || customFieldsQuery.isError) && (
        <p role="alert" className="text-xs text-red-600">
          Failed to load Encompass fields.
        </p>
      )}
      <div>
        <Button
          variant="outline"
          onClick={() =>
            onChange([...conditions, { field: '', operator: 'and', value: '' }])
          }
        >
          Add condition
        </Button>
      </div>
    </div>
  );
}
